import {
  Backdrop,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';
import ReportContent from '@components/Modal/Content/ReportContent';
import UploadRecordsContent from '@components/Modal/Content/UploadRecordsModalContent/UploadRecordsContent';

import { RECORD_UPDATE_TYPES } from '@constants/record-update-types';
import { RECORD_UPLOAD } from '@constants/record-update-types';

import { useModal } from '@hooks/useModal';
import useWindowPosition from '@hooks/useWindowPosition';

const useStyles = makeStyles((theme: Theme) => ({
  backdrop: {
    zIndex: 1,
    backgroundColor: `${theme.palette.grey['900']}36`,
  },
  speedDial: {
    position: 'fixed',
    bottom: 11,
    right: 2,
  },
}));

const messages = defineMessages({
  updateTitle: {
    id: 'speedDial.updateTitle',
    defaultMessage: 'Tell us about an encounter',
  },
  missingTitle: {
    id: 'speedDial.missingTitle',
    defaultMessage: 'Something missing?',
  },
  incorrectTitle: {
    id: 'speedDial.incorrectTitle',
    defaultMessage: 'Something not right?',
  },
  uploadRecord: {
    id: 'speedDial.uploadRecord',
    defaultMessage: 'Upload a Record',
  },
});

const FAB_SETTINGS = {
  [RECORD_UPDATE_TYPES.RECORD_UPDATE]: {
    icon: (
      <Icon
        clickable
        name="files-medical"
        style={{ fontSize: 24 }}
        type="fad"
      />
    ),
  },
  [RECORD_UPDATE_TYPES.MISSING_INFORMATION]: {
    icon: (
      <Icon
        clickable
        name="comment-question"
        style={{ fontSize: 24 }}
        type="fad"
      />
    ),
  },
  [RECORD_UPDATE_TYPES.INCORRECT_INFORMATION]: {
    icon: (
      <Icon
        clickable
        name="circle-exclamation"
        style={{ fontSize: 24 }}
        type="fad"
      />
    ),
  },
  [RECORD_UPLOAD]: {
    icon: <Icon clickable name="file" style={{ fontSize: 24 }} type="fad" />,
  },
} as const;

type OptionKeys = keyof typeof FAB_SETTINGS;

const SpeedDialTooltipOpen: React.FC<{ settings: readonly OptionKeys[] }> = ({
  settings = [],
}) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(true);
  const scrollPosition = useWindowPosition();

  const { closeModal, openModal } = useModal();

  const handleOpenModal = (type: OptionKeys) => {
    openModal({
      width: 600,
      props: { padding: '0px' },
      children:
        type === RECORD_UPLOAD ? (
          <UploadRecordsContent onSubmit={() => closeModal()} />
        ) : (
          <ReportContent onSubmit={() => closeModal()} type={type} />
        ),
    });
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const FAB_CONFIG: Record<OptionKeys, { name: string; callback: () => void }> =
    {
      [RECORD_UPDATE_TYPES.RECORD_UPDATE]: {
        name: formatMessage(messages.updateTitle),
        callback: () => handleOpenModal(RECORD_UPDATE_TYPES.RECORD_UPDATE),
      },
      [RECORD_UPDATE_TYPES.MISSING_INFORMATION]: {
        name: formatMessage(messages.missingTitle),
        callback: () =>
          handleOpenModal(RECORD_UPDATE_TYPES.MISSING_INFORMATION),
      },
      [RECORD_UPDATE_TYPES.INCORRECT_INFORMATION]: {
        name: formatMessage(messages.incorrectTitle),
        callback: () =>
          handleOpenModal(RECORD_UPDATE_TYPES.INCORRECT_INFORMATION),
      },
      [RECORD_UPLOAD]: {
        name: formatMessage(messages.uploadRecord),
        callback: () => handleOpenModal(RECORD_UPLOAD),
      },
    };

  useEffect(() => {
    if (scrollPosition > 80) {
      setOpen(false);
      setVisible(false);
    } else {
      setVisible(true);
    }
  }, [scrollPosition]);

  return (
    <div>
      <Backdrop className={classes.backdrop} open={open} />
      <SpeedDial
        FabProps={{
          style: { width: '54px', height: '54px', marginBottom: '50px' },
        }}
        ariaLabel="Actions"
        className={classes.speedDial}
        hidden={!visible}
        icon={<SpeedDialIcon />}
        onClose={handleClose}
        onOpen={handleOpen}
        open={open}
      >
        {settings.map((key: OptionKeys) => (
          <SpeedDialAction
            icon={FAB_SETTINGS[key].icon}
            key={key}
            onClick={() => {
              handleClose();
              FAB_CONFIG[key].callback();
            }}
            style={{ whiteSpace: 'nowrap' }}
            tooltipOpen
            tooltipTitle={FAB_CONFIG[key].name}
          />
        ))}
      </SpeedDial>
    </div>
  );
};

export default SpeedDialTooltipOpen;
